// Minimal strict schema DSL for the Creative Document (Checkpoint 7.6B).
//
// One declaration gives three things: structural validation with stable
// issue paths, the inferred TypeScript type, and a fresh output value that
// contains ONLY declared keys (unknown keys are rejected, never copied).
// Nothing here evaluates input: values are plain JSON, inspected and copied.
//
// Not a general-purpose library on purpose: the surface is exactly what the
// accepted Creative Document (ADR-0018) needs.

import type { IssueSink } from "../issues.js";

export const FAIL: unique symbol = Symbol("creative-model.fail");
export type Fail = typeof FAIL;

export interface Schema<T> {
  readonly parse: (value: unknown, path: string, sink: IssueSink) => T | Fail;
  /**
   * Machine-readable description of what `parse` accepts (7.6G.2): a JSON Schema
   * fragment built from the SAME declaration, so contracts derived from it (AI
   * build contracts) cannot drift from the validator. Cross-field checks of
   * `refine` are not expressible and are named in `description`.
   */
  readonly describe?: () => JsonSchema;
}

/** The JSON Schema subset the DSL produces (draft 2020-12 keywords). */
export interface JsonSchema {
  readonly type?: "string" | "number" | "integer" | "boolean" | "object" | "array" | "null";
  readonly description?: string;
  readonly properties?: Readonly<Record<string, JsonSchema>>;
  readonly required?: readonly string[];
  readonly additionalProperties?: false | JsonSchema;
  readonly propertyNames?: JsonSchema;
  readonly minProperties?: number;
  readonly maxProperties?: number;
  readonly items?: JsonSchema;
  readonly minItems?: number;
  readonly maxItems?: number;
  readonly uniqueItems?: boolean;
  readonly enum?: readonly (string | number | boolean)[];
  readonly const?: string | number | boolean;
  readonly anyOf?: readonly JsonSchema[];
  readonly minimum?: number;
  readonly maximum?: number;
  readonly multipleOf?: number;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly pattern?: string;
  /** Discriminator of a DSL union (the member is chosen by this property's const). */
  readonly "x-discriminator"?: string;
  /** Number of decimal places accepted (DSL `decimals`). */
  readonly "x-decimals"?: number;
}

/** JSON Schema of a DSL schema; `{}` (anything) when the schema carries no description. */
export function describeSchema(schema: Schema<unknown>): JsonSchema {
  return schema.describe ? schema.describe() : {};
}

/** Attaches a description to a hand-written schema (custom `parse`) at its declaration site. */
export function described<T, S extends Schema<T>>(schema: S, describe: () => JsonSchema): S {
  return { ...schema, describe };
}
export interface OptionalSchema<T> extends Schema<T> {
  readonly optional: true;
}

export type Infer<S> = S extends Schema<infer T> ? T : never;

type Shape = Readonly<Record<string, Schema<unknown>>>;
type OptionalKeys<T extends Shape> = { [K in keyof T]: T[K] extends OptionalSchema<unknown> ? K : never }[keyof T];
type RequiredKeys<T extends Shape> = Exclude<keyof T, OptionalKeys<T>>;
type Simplify<T> = { [K in keyof T]: T[K] } & {};
export type InferObject<T extends Shape> = Simplify<
  { readonly [K in RequiredKeys<T>]: Infer<T[K]> } & { readonly [K in OptionalKeys<T>]?: Infer<T[K]> }
>;

/** Identifier pattern shared by every id namespace of the document. */
export const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** Names that would be dangerous as keys of a plain object built from input. */
export const RESERVED_IDS: ReadonlySet<string> = new Set(["__proto__", "constructor", "prototype"]);

/** Unknown keys with these names are reported as executable content, not just as unknown fields. */
const EXECUTABLE_KEY =
  /^(on[a-z].*|script|scripts|html|innerhtml|outerhtml|code|js|javascript|eval|function|expression|customcode|custom_code|css|rawcss|raw_css|svgmarkup|markup)$/i;

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function describeKey(key: string): string {
  const safe = key.length > 64 ? `${key.slice(0, 64)}…` : key;
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(safe) ? `.${safe}` : `[${JSON.stringify(safe)}]`;
}

export function childPath(path: string, key: string | number): string {
  return typeof key === "number" ? `${path}[${String(key)}]` : `${path}${describeKey(key)}`;
}

const typeName = (v: unknown): string => (v === null ? "null" : Array.isArray(v) ? "array" : typeof v);

function invalidType(sink: IssueSink, path: string, expected: string, value: unknown): Fail {
  sink.add("INVALID_TYPE", path, `Expected ${expected}.`, { expected, received: typeName(value) });
  return FAIL;
}

export function optional<T>(schema: Schema<T>): OptionalSchema<T> {
  return { parse: schema.parse, optional: true, ...(schema.describe ? { describe: schema.describe } : {}) };
}

export function string(
  opts: { min?: number; max?: number; pattern?: RegExp; patternName?: string } = {}
): Schema<string> {
  const min = opts.min ?? 0;
  const max = opts.max ?? 10_000;
  return {
    describe: () => ({
      type: "string",
      ...(min > 0 ? { minLength: min } : {}),
      maxLength: max,
      ...(opts.pattern ? { pattern: opts.pattern.source } : {}),
      ...(opts.patternName ? { description: opts.patternName } : {}),
    }),
    parse(value, path, sink) {
      if (typeof value !== "string") return invalidType(sink, path, "string", value);
      if (value.length < min) {
        sink.add(min === 1 ? "REQUIRED" : "TOO_SHORT", path, `Must have at least ${min} character(s).`, { min });
        return FAIL;
      }
      if (value.length > max) {
        sink.add("TOO_LONG", path, `Must have at most ${max} characters.`, { max });
        return FAIL;
      }
      if (opts.pattern && !opts.pattern.test(value)) {
        sink.add(
          "INVALID_FORMAT",
          path,
          `Does not match the expected format${opts.patternName ? ` (${opts.patternName})` : ""}.`,
          {
            format: opts.patternName ?? "pattern",
          }
        );
        return FAIL;
      }
      return value;
    },
  };
}

export function id(): Schema<string> {
  const base = string({ min: 1, max: 64, pattern: ID_PATTERN, patternName: "id" });
  return {
    describe: () => ({ ...describeSchema(base), description: "id (letters, digits, _ or -; not __proto__)" }),
    parse(value, path, sink) {
      const out = base.parse(value, path, sink);
      if (out === FAIL) return FAIL;
      if (RESERVED_IDS.has(out)) {
        sink.add("RESERVED_ID", path, "This identifier is reserved.", {});
        return FAIL;
      }
      return out;
    },
  };
}

export function number(
  opts: { min?: number; max?: number; integer?: boolean; decimals?: number } = {}
): Schema<number> {
  return {
    describe: () => ({
      type: opts.integer ? "integer" : "number",
      ...(opts.min !== undefined ? { minimum: opts.min } : {}),
      ...(opts.max !== undefined ? { maximum: opts.max } : {}),
      ...(opts.decimals !== undefined && !opts.integer ? { "x-decimals": opts.decimals } : {}),
    }),
    parse(value, path, sink) {
      if (typeof value !== "number") return invalidType(sink, path, "number", value);
      if (!Number.isFinite(value)) {
        sink.add("INVALID_VALUE", path, "Must be a finite number.", {});
        return FAIL;
      }
      if (opts.integer && !Number.isInteger(value)) {
        sink.add("INVALID_VALUE", path, "Must be an integer.", { integer: true });
        return FAIL;
      }
      if (opts.decimals !== undefined) {
        const f = 10 ** opts.decimals;
        if (Math.round(value * f) / f !== value) {
          sink.add("INVALID_VALUE", path, `Must have at most ${opts.decimals} decimal places.`, {
            decimals: opts.decimals,
          });
          return FAIL;
        }
      }
      if (opts.min !== undefined && value < opts.min) {
        sink.add("OUT_OF_RANGE", path, `Must be ≥ ${opts.min}.`, { min: opts.min });
        return FAIL;
      }
      if (opts.max !== undefined && value > opts.max) {
        sink.add("OUT_OF_RANGE", path, `Must be ≤ ${opts.max}.`, { max: opts.max });
        return FAIL;
      }
      return value === 0 ? 0 : value; // normalizes -0
    },
  };
}

export const integer = (opts: { min?: number; max?: number } = {}): Schema<number> =>
  number({ ...opts, integer: true });

export function boolean(): Schema<boolean> {
  return {
    describe: () => ({ type: "boolean" }),
    parse: (value, path, sink) => (typeof value === "boolean" ? value : invalidType(sink, path, "boolean", value)),
  };
}

export function literal<const T extends string | number | boolean>(expected: T): Schema<T> {
  return {
    describe: () => ({
      type: typeof expected === "string" ? "string" : typeof expected === "boolean" ? "boolean" : "number",
      const: expected,
    }),
    parse(value, path, sink) {
      if (value !== expected) {
        sink.add("INVALID_VALUE", path, `Must be ${JSON.stringify(expected)}.`, { expected: String(expected) });
        return FAIL;
      }
      return expected;
    },
  };
}

export function oneOf<const T extends readonly string[]>(values: T): Schema<T[number]> {
  return {
    describe: () => ({ type: "string", enum: values }),
    parse(value, path, sink) {
      if (typeof value !== "string") return invalidType(sink, path, "string", value);
      if (!values.includes(value)) {
        sink.add("INVALID_ENUM", path, `Must be one of: ${values.join(", ")}.`, { allowed: values.join("|") });
        return FAIL;
      }
      return value;
    },
  };
}

export function array<T>(
  item: Schema<T>,
  opts: { min?: number; max?: number; unique?: boolean } = {}
): Schema<readonly T[]> {
  const max = opts.max ?? 1_000;
  return {
    describe: () => ({
      type: "array",
      items: describeSchema(item),
      ...(opts.min !== undefined ? { minItems: opts.min } : {}),
      maxItems: max,
      ...(opts.unique ? { uniqueItems: true } : {}),
    }),
    parse(value, path, sink) {
      if (!Array.isArray(value)) return invalidType(sink, path, "array", value);
      if (opts.min !== undefined && value.length < opts.min) {
        sink.add(
          opts.min === 1 && value.length === 0 ? "REQUIRED" : "TOO_FEW",
          path,
          `Must have at least ${opts.min} item(s).`,
          {
            min: opts.min,
          }
        );
        return FAIL;
      }
      if (value.length > max) {
        sink.add("TOO_MANY", path, `Must have at most ${max} items.`, { max });
        return FAIL;
      }
      const out: T[] = [];
      let failed = false;
      for (const [i, entry] of (value as unknown[]).entries()) {
        const parsed = item.parse(entry, childPath(path, i), sink);
        if (parsed === FAIL) failed = true;
        else out.push(parsed);
      }
      if (failed) return FAIL;
      if (opts.unique && new Set(out.map((v) => JSON.stringify(v))).size !== out.length) {
        sink.add("DUPLICATE_VALUE", path, "Items must be unique.", {});
        return FAIL;
      }
      return out;
    },
  };
}

/** Strict object: unknown keys are rejected; the output holds only declared keys (fresh object). */
export function object<T extends Shape>(shape: T): Schema<InferObject<T>> {
  const keys = Object.keys(shape);
  return {
    describe: () => ({
      type: "object",
      properties: Object.fromEntries(keys.map((k) => [k, describeSchema(shape[k] as Schema<unknown>)])),
      required: keys.filter((k) => !(shape[k] as { optional?: true }).optional),
      additionalProperties: false,
    }),
    parse(value, path, sink) {
      if (!isPlainObject(value)) return invalidType(sink, path, "object", value);
      let failed = false;
      for (const key of Object.keys(value)) {
        if (Object.hasOwn(shape, key)) continue;
        failed = true;
        if (EXECUTABLE_KEY.test(key))
          sink.add(
            "EXECUTABLE_CONTENT_NOT_ALLOWED",
            childPath(path, key),
            "Executable or raw markup content is not allowed.",
            {}
          );
        else sink.add("UNKNOWN_FIELD", childPath(path, key), "Unknown field.", {});
      }
      const out: Record<string, unknown> = {};
      for (const key of keys) {
        const schema = shape[key] as Schema<unknown> & { optional?: true };
        const present = Object.hasOwn(value, key) && value[key] !== undefined;
        if (!present) {
          if (!schema.optional) {
            sink.add("REQUIRED", childPath(path, key), "Required field is missing.", {});
            failed = true;
          }
          continue;
        }
        const parsed = schema.parse(value[key], childPath(path, key), sink);
        if (parsed === FAIL) failed = true;
        else out[key] = parsed;
      }
      return failed ? FAIL : (out as InferObject<T>);
    },
  };
}

type UnionMembers = Readonly<Record<string, Schema<unknown>>>;

/** Discriminated union on `key`; each member schema must itself declare `key` as a literal. */
export function union<M extends UnionMembers>(key: string, members: M): Schema<Infer<M[keyof M]>> {
  const allowed = Object.keys(members);
  return {
    describe: () => ({
      anyOf: allowed.map((tag) => describeSchema(members[tag] as Schema<unknown>)),
      "x-discriminator": key,
    }),
    parse(value, path, sink) {
      if (!isPlainObject(value)) return invalidType(sink, path, "object", value);
      const tag = value[key];
      if (typeof tag !== "string" || !Object.hasOwn(members, tag)) {
        sink.add(
          typeof tag === "string" ? "UNSUPPORTED_KIND" : "REQUIRED",
          childPath(path, key),
          `Must be one of: ${allowed.join(", ")}.`,
          { allowed: allowed.join("|") }
        );
        return FAIL;
      }
      return (members[tag] as Schema<Infer<M[keyof M]>>).parse(value, path, sink);
    },
  };
}

/** Record keyed by document ids (validated, never reserved); built with fromEntries (no prototype writes). */
export function record<T>(
  value: Schema<T>,
  opts: { max?: number; min?: number } = {}
): Schema<Readonly<Record<string, T>>> {
  const keySchema = id();
  const max = opts.max ?? 1_000;
  return {
    describe: () => ({
      type: "object",
      propertyNames: describeSchema(keySchema),
      additionalProperties: describeSchema(value),
      ...(opts.min !== undefined ? { minProperties: opts.min } : {}),
      maxProperties: max,
    }),
    parse(input, path, sink) {
      if (!isPlainObject(input)) return invalidType(sink, path, "object", input);
      const entries = Object.keys(input);
      if (entries.length > max) {
        sink.add("TOO_MANY", path, `Must have at most ${max} entries.`, { max });
        return FAIL;
      }
      if (opts.min !== undefined && entries.length < opts.min) {
        sink.add("TOO_FEW", path, `Must have at least ${opts.min} entries.`, { min: opts.min });
        return FAIL;
      }
      const out: Array<[string, T]> = [];
      let failed = false;
      for (const key of entries) {
        const p = childPath(path, key);
        if (keySchema.parse(key, p, sink) === FAIL) {
          failed = true;
          continue;
        }
        const parsed = value.parse(input[key], p, sink);
        if (parsed === FAIL) failed = true;
        else out.push([key, parsed]);
      }
      return failed ? FAIL : Object.fromEntries(out);
    },
  };
}

/** Deferred schema for recursive structures. */
let lazyDepth = 0;
/** Recursive structures are described down to this depth (deeper levels: `{}` = see the validator). */
export const LAZY_DESCRIBE_DEPTH = 2;

export function lazy<T>(factory: () => Schema<T>): Schema<T> {
  let cached: Schema<T> | undefined;
  return {
    describe() {
      if (lazyDepth >= LAZY_DESCRIBE_DEPTH)
        return { description: "recursive (deeper levels checked by the validator)" };
      lazyDepth++;
      try {
        cached ??= factory();
        return describeSchema(cached);
      } finally {
        lazyDepth--;
      }
    },
    parse(value, path, sink) {
      cached ??= factory();
      return cached.parse(value, path, sink);
    },
  };
}

/** Adds a post-parse check (cross-field rules local to one value). */
export function refine<T>(
  schema: Schema<T>,
  check: (value: T, path: string, sink: IssueSink) => boolean,
  /** What the check adds, as JSON Schema keywords and/or a description (declared next to the check). */
  extra: Partial<JsonSchema> = {}
): Schema<T> {
  return {
    describe: () => {
      const base = describeSchema(schema);
      return {
        ...base,
        ...extra,
        ...(extra.description || base.description
          ? { description: [base.description, extra.description].filter(Boolean).join("; ") }
          : {}),
      };
    },
    parse(value, path, sink) {
      const out = schema.parse(value, path, sink);
      if (out === FAIL) return FAIL;
      return check(out, path, sink) ? out : FAIL;
    },
  };
}
